/*
 * TIJDELIJKE FIX — 08-09-2026
 *
 * Het blok "Vaak samen besteld" komt van de app "Essential Upsell & Cross Sell".
 * Shopify ondersteunt geen app-blokken in de cart drawer, dus de app spuit haar
 * markup daar met JavaScript in, binnen <cart-items> van het theme.
 *
 * Wat er misgaat: hun Toevoegen-knop doet zelf een geslaagde POST naar
 * /cart/add.js en herlaadt daarna de pagina. Daardoor gaat de drawer dicht en
 * lijkt het alsof je wordt weggestuurd. Het is dus geen form-submit en geen
 * ontbrekende preventDefault, maar een bewuste reload van de app. Vastgesteld met
 * een netwerk- en DOM-log: de add antwoordt 200, daarna volgt de navigatie.
 *
 * Deze laag vangt die klik af in de capture-fase op window, waardoor hun eigen
 * onClick nooit draait en de reload dus ook niet, en voert de add zelf uit. De
 * line item properties worden uit hun markup overgenomen, want de upsellkorting
 * hangt daar vermoedelijk aan.
 *
 * Selectie gaat op hun data-attributen, niet op class-namen: die zijn generated
 * (x1ghz6dp en dergelijke) en veranderen bij elke build.
 *
 * Gemeld bij de support van Essential Apps. Weghalen zodra zij het oplossen:
 * verwijder dit bestand en de <script>-regel in snippets/cart-drawer.liquid.
 */

/* global theme */

(() => {
  const BUTTON = '[data-essential-upsell-element="add-to-cart-button"]';
  const PRODUCT = '[data-essential-upsell-element="product"]';
  const CONTAINER = '[data-essential-upsell-element="container"]';

  /**
   * Reads the variant the offer would add.
   * @param {Element} product - The offer's product element.
   * @returns {number}
   */
  function getVariantId(product) {
    const select = product.querySelector('select');
    if (select && select.value) return Number(select.value);

    const el = product.querySelector('[data-variant-id]');
    return el ? Number(el.dataset.variantId) : 0;
  }

  /**
   * Rebuilds the line item properties the app sends, so its attribution and any
   * discount tied to the funnel keep working.
   * @param {Element} product - The offer's product element.
   * @returns {?object}
   */
  function getProperties(product) {
    const container = product.closest(CONTAINER);
    const funnelId = container ? container.dataset.essentialUpsellFunnelId : null;
    const withProductId = product.querySelector('[data-product-id]');
    const productId = withProductId ? withProductId.dataset.productId : null;

    if (!funnelId && !productId) return null;

    const properties = {};
    if (funnelId) properties.__essential_upsell_funnel_id = funnelId;
    if (productId) properties.__essential_upsell_product_id = productId;

    return properties;
  }

  /**
   * Adds the offer to the cart and updates the drawer, without a page reload.
   * @param {object} item - Line item to add.
   * @param {Element} trigger - The button that was clicked.
   */
  async function addToCart(item, trigger) {
    if (trigger.dataset.ajaxBusy) return;
    trigger.dataset.ajaxBusy = 'true';
    trigger.setAttribute('aria-busy', 'true');
    trigger.disabled = true;

    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      });
      if (!response.ok) throw new Error(response.status);

      const cartResponse = await fetch(`${theme.routes.cart}.js`);
      const cart = await cartResponse.json();

      // Hertekenen is niet optioneel: het theme werkt aantallen bij op line-nummer
      // en de nieuwe regel komt vooraan te staan, dus zonder dit wijst data-index
      // na een add naar het verkeerde product.
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

      // Meldt de toevoeging aan de rest van het theme, waaronder het cadeau-blok.
      document.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { cart, variantId: item.id }
      }));
    } catch (error) {
      console.log(error); // eslint-disable-line
    } finally {
      delete trigger.dataset.ajaxBusy;
      trigger.removeAttribute('aria-busy');
      trigger.disabled = false;
    }
  }

  /**
   * Intercepts a click on the app's add-to-cart button.
   * @param {object} evt - Event object.
   */
  function handleClick(evt) {
    if (!(evt.target instanceof Element)) return;

    const trigger = evt.target.closest(BUTTON);
    if (!trigger) return;

    const product = trigger.closest(PRODUCT);
    if (!product) return;

    const id = getVariantId(product);

    // Geen variant te vinden? Dan laten we de klik met rust in plaats van hem
    // te breken; de app doet dan gewoon wat ze altijd deed.
    if (!id) return;

    evt.preventDefault();
    evt.stopPropagation();

    const properties = getProperties(product);
    const item = { id, quantity: 1 };
    if (properties) item.properties = properties;

    addToCart(item, trigger);
  }

  // Op window in de capture-fase: dat loopt vóór de listener die de app op de knop
  // zelf zet, ook al wordt die knop pas later ingespoten.
  window.addEventListener('click', handleClick, true);
})();
